import React from 'react';

import { Group, Paper, Stack, Text } from '@mantine/core';
import { IconUserFilled } from '@tabler/icons-react';
import AthleteClassStateStack from 'components/AthleteClass/AthleteClassStateStack';
import { IEventTypeMultiSelectAutoComplete } from 'components/form-fields/auto-complete-fields/EventTypeMultiSelectAutoComplete';
import { useAuth } from 'context/auth-context';
import dayjs from 'dayjs';
import useAthleteClassMeta from 'hooks/athleteClass/useAthleteClassMeta';
import useAthleteClassState from 'hooks/athleteClass/useAthleteClassState';
import { useClassMeta } from 'hooks/class/useClassMeta';
import { useNavigate } from 'react-router-dom';
import { KiloClassAthleteAgenda } from 'types';

interface ClassCardProps {
  kiloClass: KiloClassAthleteAgenda;
  selectedDate: Date;
  searchFilters: {
    eventTypes: IEventTypeMultiSelectAutoComplete[];
    coaches: string[];
    locations: string[];
  };
}

const ClassCard = ({ kiloClass, selectedDate, searchFilters }: ClassCardProps): JSX.Element => {
  const navigate = useNavigate();
  const { session } = useAuth();

  const athleteKiloClass = kiloClass.athleteKiloClasses.find(
    akc => akc.athlete === session?.athlete?.athleteId,
  );

  const athleteClassState = useAthleteClassState(athleteKiloClass);
  const classMeta = useClassMeta(kiloClass);
  const athleteClassMeta = useAthleteClassMeta(athleteClassState, classMeta);

  const numCoaches = kiloClass.kiloClassCoachKiloClassCoachRoles.length;
  const coach = numCoaches > 0 ? kiloClass.kiloClassCoachKiloClassCoachRoles[0].coach.fullName : '';
  const coachText = numCoaches > 1 ? `${coach} +${numCoaches - 1}` : coach;
  const location = kiloClass.location?.name;

  const handleCardClick = (): void => {
    navigate(`/classes/${kiloClass.kiloClassId}`, {
      state: {
        prevPath: '/',
        calendarDate: selectedDate,
        ...searchFilters,
      },
    });
  };

  return (
    <Paper
      onClick={handleCardClick}
      bg={athleteClassMeta.backgroundColor}
      sx={{
        cursor: 'pointer',
        position: 'relative',
      }}
      shadow='xs'
      radius='lg'
      p='sm'
      px='lg'
    >
      <Group position='apart' noWrap>
        <Stack spacing={2} align='stretch' pr='xl'>
          <Text size='lg' weight='bold' lineClamp={1}>
            {kiloClass.kiloClassType.name}
          </Text>
          <Text size='sm' weight='bold' color='dimmed' sx={{ textWrap: 'nowrap' }}>
            {`${dayjs(classMeta.eventStartDateTime).format('h:mma')} - ${dayjs(classMeta.eventStartDateTime)
              .add(kiloClass.duration, 'minutes')
              .format('h:mma')}`}
          </Text>

          {(!!coachText || !!location) && (
            <Group spacing={2}>
              {coachText && location ? (
                <Text size='sm' weight='semibold' lineClamp={1} color='dimmed'>
                  {coachText} | {location}
                </Text>
              ) : (
                <Text size='sm' weight='semibold' lineClamp={1} color='dimmed'>
                  {coachText || location}
                </Text>
              )}
            </Group>
          )}
          <Text size='sm' weight={600} color='dimmed' lineClamp={1}>
            <Group spacing='xs'>
              <IconUserFilled size={16} />
              {classMeta.classSizeDisplay}
            </Group>
          </Text>
        </Stack>
        <AthleteClassStateStack athleteClassMeta={athleteClassMeta} kiloClass={kiloClass} size='md' />
      </Group>
    </Paper>
  );
};

export default ClassCard;
